/**
 * Notifications API service for managing user notifications and preferences.
 * @module lib/api/notifications.api
 */

import {
  BulkUpdateNotificationPreferencesDto,
  Notification,
  NotificationFeedResponse,
  NotificationPreference,
  NotificationQueryDto,
  NotificationSummary,
  PaginationParams,
  UnreadEntitiesResponse,
  UpdateNotificationPreferenceDto,
} from '@scrambl/shared'

import { apiClient } from './api-client'
import { buildQueryString } from './base-api'
import { createSingletonApi } from './singleton-factory'

export interface NotificationListResponse {
  data: Notification[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface SmsStatus {
  userId: string
  phoneNumber: string | null
  phoneVerified: string | null
  consentGrantedAt: string | null
  consentRevokedAt: string | null
  consentVersion: string | null
  enabled: boolean
  disclosureVersion: string
}

interface SmsRequestOptions {
  signal?: AbortSignal
  organizationId?: string
}
function smsRoute(options?: SmsRequestOptions) {
  return {
    signal: options?.signal,
    ...(options?.organizationId
      ? {
          skipOrgContext: true,
          headers: { 'x-organization-id': options.organizationId },
        }
      : {}),
  }
}

class NotificationsApiService {
  async getSmsStatus(options?: SmsRequestOptions): Promise<SmsStatus> {
    return apiClient<SmsStatus>('/notifications/sms/status', smsRoute(options))
  }
  async requestSmsVerification(
    data: { phoneNumber: string; consentAccepted: boolean },
    options?: SmsRequestOptions,
  ): Promise<{
    userId: string
    sent: boolean
    phoneNumber: string
    expiresAt: string
  }> {
    return apiClient('/notifications/sms/verification', {
      method: 'POST',
      body: data,
      ...smsRoute(options),
    })
  }
  async confirmSmsVerification(
    data: { phoneNumber: string; code: string },
    options?: SmsRequestOptions,
  ): Promise<SmsStatus> {
    return apiClient<SmsStatus>('/notifications/sms/verification/confirm', {
      method: 'POST',
      body: data,
      ...smsRoute(options),
    })
  }
  async revokeSmsConsent(options?: SmsRequestOptions): Promise<SmsStatus> {
    return apiClient<SmsStatus>('/notifications/sms/consent', {
      method: 'DELETE',
      ...smsRoute(options),
    })
  }

  /**
   * Gets paginated notifications
   * @param query - Filter and pagination parameters
   * @returns Paginated notification list
   */
  async getNotifications(
    query?: NotificationQueryDto & PaginationParams,
    options: { signal?: AbortSignal } = {},
  ): Promise<NotificationListResponse> {
    const queryString = query ? buildQueryString(query) : ''
    return apiClient<NotificationListResponse>(
      `/notifications${queryString}`,
      options,
    )
  }

  /**
   * Gets a single notification by ID
   * @param id - Notification ID
   * @returns The notification
   */
  async getNotification(
    id: string,
    options: { signal?: AbortSignal } = {},
  ): Promise<Notification> {
    return apiClient<Notification>(`/notifications/${id}`, options)
  }

  /**
   * Gets count of unread notifications
   * @returns Unread count
   */
  async getUnreadCount(
    options: { signal?: AbortSignal } = {},
  ): Promise<{ count: number }> {
    return apiClient<{ count: number }>('/notifications/unread-count', options)
  }

  /**
   * Gets notification summary
   * @returns Notification summary
   */
  /**
   * Gets the lane-based feed (action required / mentions / activity)
   */
  async getFeed(
    limit?: number,
    options: { signal?: AbortSignal } = {},
  ): Promise<NotificationFeedResponse> {
    return apiClient<NotificationFeedResponse>(
      `/notifications/feed${limit ? `?limit=${limit}` : ''}`,
      options,
    )
  }

  /**
   * Gets per-record unread rollups (task/ticket) for row indicators
   */
  async getUnreadEntities(
    options: { signal?: AbortSignal } = {},
  ): Promise<UnreadEntitiesResponse> {
    return apiClient<UnreadEntitiesResponse>(
      '/notifications/unread-entities',
      options,
    )
  }

  async getSummary(
    options: { signal?: AbortSignal } = {},
  ): Promise<NotificationSummary> {
    return apiClient<NotificationSummary>('/notifications/summary', options)
  }

  /**
   * Marks a notification as read
   * @param id - Notification ID
   * @returns Updated notification
   */
  async markAsRead(
    id: string,
    options: { signal?: AbortSignal } = {},
  ): Promise<Notification> {
    return apiClient<Notification>(`/notifications/${id}/read`, {
      method: 'PUT',
      signal: options.signal,
    })
  }

  /**
   * Marks all notifications as read
   * @returns Count of updated notifications
   */
  /**
   * Dismisses all undismissed notifications, optionally scoped to types
   * (used by the feed's per-lane "Dismiss all")
   */
  async dismissAll(
    types?: string[],
    options: { signal?: AbortSignal } = {},
  ): Promise<{ dismissed: number }> {
    return apiClient<{ dismissed: number }>('/notifications/dismiss-all', {
      method: 'POST',
      signal: options.signal,
      body: types?.length ? { types } : {},
    })
  }

  async markAllAsRead(
    options: { signal?: AbortSignal } = {},
  ): Promise<{ updated: number }> {
    return apiClient<{ updated: number }>('/notifications/mark-all-read', {
      method: 'POST',
      signal: options.signal,
    })
  }

  /**
   * Marks multiple notifications as read
   * @param notificationIds - Array of notification IDs
   * @returns Count of updated notifications
   */
  async markMultipleAsRead(
    notificationIds: string[],
    options: { signal?: AbortSignal } = {},
  ): Promise<{ updated: number }> {
    return apiClient<{ updated: number }>('/notifications/mark-read', {
      method: 'POST',
      body: { notificationIds },
      signal: options.signal,
    })
  }

  /**
   * Dismisses a notification
   * @param id - Notification ID
   */
  async dismissNotification(
    id: string,
    options: { signal?: AbortSignal } = {},
  ): Promise<Notification> {
    return apiClient<Notification>(`/notifications/${id}`, {
      method: 'DELETE',
      signal: options.signal,
    })
  }

  /**
   * Dismisses multiple notifications
   * @param notificationIds - Array of notification IDs
   * @returns Count of dismissed notifications
   */
  async dismissMultiple(
    notificationIds: string[],
    options: { signal?: AbortSignal } = {},
  ): Promise<{ updated: number }> {
    return apiClient<{ updated: number }>('/notifications/dismiss', {
      method: 'POST',
      signal: options.signal,
      body: { notificationIds },
    })
  }

  /**
   * Gets notification preferences
   * @returns Array of notification preferences
   */
  async getPreferences(
    options: SmsRequestOptions = {},
  ): Promise<NotificationPreference[]> {
    return apiClient<NotificationPreference[]>(
      '/notifications/preferences',
      smsRoute(options),
    )
  }

  /**
   * Updates a single notification preference
   * @param data - Preference update data
   * @returns Updated preference
   */
  async updatePreference(
    data: UpdateNotificationPreferenceDto,
    options: { signal?: AbortSignal } = {},
  ): Promise<NotificationPreference> {
    return apiClient<NotificationPreference>(
      `/notifications/preferences/${data.channel}/${data.type}`,
      { method: 'PUT', body: data, signal: options.signal },
    )
  }

  /**
   * Updates multiple notification preferences
   * @param data - Bulk update data
   * @returns Update result with preferences
   */
  async updateMultiplePreferences(
    data: BulkUpdateNotificationPreferencesDto,
    options: SmsRequestOptions = {},
  ): Promise<{ updated: number; preferences: NotificationPreference[] }> {
    return apiClient<{
      updated: number
      preferences: NotificationPreference[]
    }>('/notifications/preferences', {
      method: 'PUT',
      body: data,
      ...smsRoute(options),
    })
  }

  /**
   * Sets default notification preferences
   * @returns Update result with preferences
   */
  async setDefaultPreferences(options: SmsRequestOptions = {}): Promise<{
    reset: true
    organizationId: string
    userId: string
    removed: number
    updated: number
    preferences: NotificationPreference[]
  }> {
    return apiClient('/notifications/preferences/set-defaults', {
      method: 'POST',
      ...smsRoute(options),
    })
  }

  /**
   * Disables all notifications for a channel
   * @param channel - Channel name
   * @returns Count of updated preferences
   */
  async disableAllForChannel(channel: string): Promise<{ updated: number }> {
    return apiClient<{ updated: number }>(
      `/notifications/preferences/${channel}/disable-all`,
      { method: 'POST' },
    )
  }

  /**
   * Enables all notifications for a channel
   * @param channel - Channel name
   * @returns Count of updated preferences
   */
  async enableAllForChannel(channel: string): Promise<{ updated: number }> {
    return apiClient<{ updated: number }>(
      `/notifications/preferences/${channel}/enable-all`,
      { method: 'POST' },
    )
  }
}

export const notificationsApi = createSingletonApi(NotificationsApiService)
